import { useState } from 'react';
import axios from 'axios';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  CreateScheduledReportExportRequest,
  CreateScheduledReportExportRequestGcsConfig,
  CreateScheduledReportExportRequestS3Config,
  CreateScheduledReportExportRequestSftpConfig,
  ScheduledReportExportListResponse,
  ScheduledReportExportResponse,
  UpdateScheduledReportExportRequest,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

export interface TargetConfigurations {
  sftp?: CreateScheduledReportExportRequestSftpConfig;
  s3_bucket?: CreateScheduledReportExportRequestS3Config;
  gcs_bucket?: CreateScheduledReportExportRequestGcsConfig;
}

// Narrows target_type from string to the literal union and target_configurations to typed dict
export interface ScheduledReportExport
  extends Omit<ScheduledReportExportResponse, 'target_type' | 'target_configurations'> {
  target_type: CreateScheduledReportExportRequest['target_type'];
  target_configurations: TargetConfigurations;
}

export const useScheduledReportExports = () => {
  // @ts-expect-error useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/reports/scheduled`;

  const {
    isLoading,
    isError,
    data: schedulesData,
    reloadData: reloadSchedules,
  } = useDataFetcher<ScheduledReportExportListResponse>(baseRoute);

  // Cast narrows target_type from generic string to the expected literal union
  const schedules = (schedulesData?.schedules || []) as ScheduledReportExport[];

  const createSchedule = async (request: CreateScheduledReportExportRequest): Promise<void> => {
    try {
      setIsSubmitting(true);
      await axios.post(baseRoute, request);
      await reloadSchedules();
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateSchedule = async (scheduleId: number, request: UpdateScheduledReportExportRequest): Promise<void> => {
    try {
      setIsSubmitting(true);
      await axios.patch(`${baseRoute}/${scheduleId}`, request);
      await reloadSchedules();
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteSchedule = async (scheduleId: number): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      await axios.delete(`${baseRoute}/${scheduleId}`);
      await reloadSchedules();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const setIsActive = async (scheduleId: number, isActive: boolean): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      await axios.post(`${baseRoute}/${scheduleId}/is_active`, { is_active: isActive });
      await reloadSchedules();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    schedules,
    isLoading,
    isError,
    reloadSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    setIsActive,
    isSubmitting,
  };
};
